import React, { useState, useEffect } from 'react';
import { analyticsService } from '../../services/analytics.service';
import { MonthlyCompanyMetrics, KpiQueryParams, MonthlyDataPoint } from '../../types/kpi.types';
import { Loader } from '../Loader';

interface MonthlyAnalysisProps {
  queryParams: KpiQueryParams;
}

const formatCurrency = (amount: number): string => {
  if (amount >= 1000000) return `₪${(amount / 1000000).toFixed(1)}M`;
  if (amount >= 1000) return `₪${(amount / 1000).toFixed(1)}K`;
  return `₪${amount.toFixed(0)}`;
};

const formatMonth = (month: string): string => {
  const [year, m] = month.split('-');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[parseInt(m) - 1]} '${year.slice(2)}`;
};

export const MonthlyAnalysis: React.FC<MonthlyAnalysisProps> = ({ queryParams }) => {
  const [data, setData] = useState<MonthlyCompanyMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        const result = await analyticsService.getMonthlyCompanyMetrics(queryParams);
        setData(result);
      } catch (err: any) {
        setError(err.message || 'Failed to load monthly data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [queryParams.startDate, queryParams.endDate, queryParams.companyId, queryParams.database]);

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: '#ff4444', backgroundColor: '#fff', borderRadius: '12px' }}>
        <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
        <div style={{ fontSize: '18px', fontWeight: 600 }}>Error Loading Data</div>
        <div style={{ fontSize: '14px', marginTop: '8px' }}>{error}</div>
      </div>
    );
  }

  if (!data || data.companies.length === 0) {
    return <div style={{ padding: '40px', textAlign: 'center' }}>No data available</div>;
  }

  // Calculate cumulative totals for context and normalize to MonthlyDataPoint[]
  let cumulativeUsers = 0;
  const monthlyTotalsWithCumulative: MonthlyDataPoint[] = data.totals.map(t => {
    cumulativeUsers += t.newUsers;
    return {
      month: t.month,
      mau: t.mau,
      newUsers: t.newUsers,
      returningUsers: t.returningUsers,
      transfers: t.transfers,
      amount: t.amount,
      newUserAmount: t.newUserAmount,
      returningUserAmount: t.returningUserAmount,
      // totals don't have transfer counts split, so default to 0
      newUserTransfers: 0,
      returningUserTransfers: 0,
      avgAmountPerNewUser: t.avgAmountPerNewUser,
      avgAmountPerReturningUser: t.avgAmountPerReturningUser,
    };
  });

  // Get selected company data or totals (from global selector), always as MonthlyDataPoint[]
  const rawCompanyData: MonthlyDataPoint[] = !queryParams.companyId
    ? monthlyTotalsWithCumulative
    : data.companies.find(c => c.id === queryParams.companyId)?.monthlyData || [];

  // Filter to only show months with activity (mau > 0) for specific companies
  // For "All Companies" view, show all months
  const displayData = !queryParams.companyId
    ? rawCompanyData
    : rawCompanyData.filter((m: any) => m.mau > 0);

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ margin: 0, fontSize: '24px', fontWeight: 600, color: '#333' }}>
          📅 Monthly Transfer Activity
        </h2>
        <p style={{ margin: '8px 0 0 0', color: '#666', fontSize: '14px' }}>
          Track unique users, transfer volumes, and amounts by month - new vs returning users
        </p>
      </div>

      {/* Key Metrics Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
      }}>
        <MetricCard
          title="Total Active User-Months"
          value={displayData.reduce((sum: number, d: any) => sum + d.mau, 0)}
          subtitle="sum of monthly unique active users"
          color="#1a237e"
          icon="👥"
        />
        <MetricCard
          title="Total Amount"
          value={formatCurrency(displayData.reduce((sum: number, d: any) => sum + d.amount, 0))}
          subtitle="transferred"
          color="#f57c00"
          icon="💰"
          isString
        />
        <MetricCard
          title="Avg/New"
          value={(() => {
            const totalNewAmount = displayData.reduce((sum: number, d: any) => sum + (d.newUserAmount || 0), 0);
            const totalNewUsers = displayData.reduce((sum: number, d: any) => sum + (d.newUsers || 0), 0);
            const avg = totalNewUsers > 0 ? totalNewAmount / totalNewUsers : 0;
            return formatCurrency(avg);
          })()}
          subtitle="average amount per new user"
          color="#2e7d32"
          icon="📊"
          isString
        />
        <MetricCard
          title="Avg/Return"
          value={(() => {
            const totalReturnAmount = displayData.reduce((sum: number, d: any) => sum + (d.returningUserAmount || 0), 0);
            const totalReturnTransfers = displayData.reduce((sum: number, d: any) => sum + (d.returningUserTransfers || 0), 0);
            const avg = totalReturnTransfers > 0 ? totalReturnAmount / totalReturnTransfers : 0;
            return formatCurrency(avg);
          })()}
          subtitle="average amount per returning transfer"
          color="#1565c0"
          icon="📊"
          isString
        />
      </div>

      {/* Monthly Breakdown Table */}
      <div style={{
        backgroundColor: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: '#f8f9fa',
        }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: '#333' }}>
            Monthly Breakdown (Unique Users)
          </h3>
        </div>
        
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#333', borderBottom: '2px solid #e0e0e0' }}>
                  Month
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#1a237e', borderBottom: '2px solid #e0e0e0' }}>
                  👥 Total
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#2e7d32', borderBottom: '2px solid #e0e0e0' }}>
                  🆕 New
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#2e7d32', borderBottom: '2px solid #e0e0e0', fontSize: '12px' }}>
                  % New
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#2e7d32', borderBottom: '2px solid #e0e0e0', fontSize: '12px' }}>
                  💰 New Amt
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#2e7d32', borderBottom: '2px solid #e0e0e0', fontSize: '12px' }}>
                  Avg/New
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#1565c0', borderBottom: '2px solid #e0e0e0' }}>
                  🔄 Return
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#1565c0', borderBottom: '2px solid #e0e0e0', fontSize: '12px' }}>
                  % Return
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#1565c0', borderBottom: '2px solid #e0e0e0', fontSize: '12px' }}>
                  💰 Return Amt
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#666', borderBottom: '2px solid #e0e0e0', fontSize: '12px' }}>
                  Total Transfers
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#666', borderBottom: '2px solid #e0e0e0', fontSize: '12px' }}>
                  Return Transfers
                </th>
                <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#1565c0', borderBottom: '2px solid #e0e0e0', fontSize: '12px' }}>
                  Avg/Return
                </th>
              </tr>
            </thead>
            <tbody>
              {displayData.map((row: any, idx: number) => {
                const prevRow = idx > 0 ? displayData[idx - 1] : null;
                const userGrowth = prevRow ? row.mau - prevRow.mau : 0;
                
                return (
                  <tr key={row.month} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafafa' }}>
                    <td style={{ padding: '12px 16px', fontWeight: 600, color: '#333', borderBottom: '1px solid #eee' }}>
                      {formatMonth(row.month)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      <span style={{ fontSize: '16px', fontWeight: 700, color: '#1a237e' }}>
                        {row.mau}
                      </span>
                      {userGrowth !== 0 && (
                        <span style={{ 
                          marginLeft: '6px', 
                          fontSize: '11px', 
                          color: userGrowth > 0 ? '#2e7d32' : '#c62828',
                          fontWeight: 600,
                        }}>
                          {userGrowth > 0 ? '↑' : '↓'}{Math.abs(userGrowth)}
                        </span>
                      )}
                    </td>
                    {/* New Users */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      <div style={{ 
                        display: 'inline-block',
                        backgroundColor: '#e8f5e9',
                        color: '#2e7d32',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontWeight: 700,
                        fontSize: '13px',
                      }}>
                        {row.newUsers}
                      </div>
                    </td>
                    {/* % New */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#2e7d32', fontSize: '13px', fontWeight: 500 }}>
                      {row.mau > 0 ? ((row.newUsers / row.mau) * 100).toFixed(1) + '%' : '-'}
                    </td>
                    {/* New User Amount */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#2e7d32', fontSize: '13px' }}>
                      {formatCurrency(row.newUserAmount || 0)}
                    </td>
                    {/* Avg per New User */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#666', fontSize: '12px' }}>
                      {row.avgAmountPerNewUser ? formatCurrency(row.avgAmountPerNewUser) : '-'}
                    </td>
                    {/* Returning Users */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee' }}>
                      <div style={{ 
                        display: 'inline-block',
                        backgroundColor: '#e3f2fd',
                        color: '#1565c0',
                        padding: '4px 12px',
                        borderRadius: '16px',
                        fontWeight: 700,
                        fontSize: '13px',
                      }}>
                        {row.returningUsers}
                      </div>
                    </td>
                    {/* % Return */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#1565c0', fontSize: '13px', fontWeight: 500 }}>
                      {row.mau > 0 ? ((row.returningUsers / row.mau) * 100).toFixed(1) + '%' : '-'}
                    </td>
                    {/* Returning User Amount */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#1565c0', fontSize: '13px' }}>
                      {formatCurrency(row.returningUserAmount || 0)}
                    </td>
                    {/* Total Transfers */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#666', fontSize: '13px', fontWeight: 500 }}>
                      {row.transfers || 0}
                    </td>
                    {/* Return Transfers */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#666', fontSize: '13px', fontWeight: 500 }}>
                      {row.returningUserTransfers || 0}
                    </td>
                    {/* Avg per Returning User */}
                    <td style={{ padding: '12px 16px', textAlign: 'center', borderBottom: '1px solid #eee', color: '#666', fontSize: '12px' }}>
                      {row.avgAmountPerReturningUser ? formatCurrency(row.avgAmountPerReturningUser) : '-'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Explanation Box */}
      <div style={{
        marginTop: '24px',
        backgroundColor: '#f5f5f5',
        borderRadius: '12px',
        padding: '20px',
      }}>
        <h4 style={{ margin: '0 0 16px 0', fontSize: '14px', fontWeight: 600, color: '#333' }}>
          📖 Column Explanations
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
          <InfoBox
            color="#2e7d32"
            bgColor="#e8f5e9"
            title="🆕 New Users & Their Amount"
            text="Users making their FIRST EVER transfer, total amount they transferred, and average per new user."
          />
          <InfoBox
            color="#1565c0"
            bgColor="#e3f2fd"
            title="🔄 Returning Users & Their Amount"
            text="Users who transferred before and came back, their total amount, and average per returning user."
          />
          <InfoBox
            color="#f57c00"
            bgColor="#fff3e0"
            title="💡 Avg/User"
            text="Average transfer amount per user segment. Compare to see if new or returning users transfer more."
          />
        </div>
      </div>

    </div>
  );
};

// Helper Components
const MetricCard: React.FC<{
  title: string;
  value: number | string;
  subtitle: string;
  color: string;
  icon: string;
  isString?: boolean;
}> = ({ title, value, subtitle, color, icon, isString }) => (
  <div style={{
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
      <span style={{ fontSize: '20px' }}>{icon}</span>
      <span style={{ fontSize: '13px', color: '#666' }}>{title}</span>
    </div>
    <div style={{ fontSize: '28px', fontWeight: 700, color }}>
      {isString ? value : (value as number).toLocaleString()}
    </div>
    <div style={{ fontSize: '12px', color: '#888' }}>{subtitle}</div>
  </div>
);

const InfoBox: React.FC<{
  color: string;
  bgColor: string;
  title: string;
  text: string;
}> = ({ color, bgColor, title, text }) => (
  <div style={{
    backgroundColor: bgColor,
    borderRadius: '8px',
    padding: '12px 16px',
    borderLeft: `4px solid ${color}`,
  }}>
    <div style={{ fontWeight: 600, color, marginBottom: '4px', fontSize: '13px' }}>{title}</div>
    <div style={{ fontSize: '12px', color: '#666' }}>{text}</div>
  </div>
);

const MiniStat: React.FC<{
  value: number;
  label: string;
  color: string;
}> = ({ value, label, color }) => (
  <div style={{ textAlign: 'center' }}>
    <div style={{ fontSize: '18px', fontWeight: 700, color }}>{value}</div>
    <div style={{ fontSize: '10px', color: '#888' }}>{label}</div>
  </div>
);


